import { styled } from '@linaria/react'
import { ButtonSC } from 'atoms/Button'
import { ButtonGroupWrapper } from 'blocks/ButtonGroup'
import { CountdownTimerSC } from 'blocks/CountdownTimer'
import { FIXED_INDENTS, MEDIA } from 'vars/index'
import { Description } from '../../Description'

/**
 * `banner-description` from the content details concept: the title, the label
 * group and the action buttons stacked in the bottom-left corner of the banner.
 */
export const BannerDescriptionSC = styled(Description)`
  --bannerDescriptionPaddingVertical: var(${FIXED_INDENTS.INDENT_160});
  --bannerDescriptionPaddingHorizontal: var(${FIXED_INDENTS.INDENT_200});
  --bannerDescriptionRowGap: var(${FIXED_INDENTS.INDENT_80});
  --bannerDescriptionButtonsColumnGap: var(${FIXED_INDENTS.INDENT_30});
  --mcTitleWithLabelsRowGap: var(${FIXED_INDENTS.INDENT_30});
  --mcLabelsGroupGap: var(${FIXED_INDENTS.INDENT_60});
  --mcContentBadgesGroupGap: var(${FIXED_INDENTS.INDENT_20});
  --mcLabelsGroupWrap: wrap;
  /* new/body/caption tracking: 3% of the 14px label font size */
  --mcLabelsGroupLetterSpacing: 0.42px;

  && {
    box-sizing: border-box;
    width: 720px;
    max-width: min(748px, 100%);
    justify-content: flex-end;
    padding: var(--bannerDescriptionPaddingVertical)
      var(--bannerDescriptionPaddingHorizontal);
    row-gap: var(--bannerDescriptionRowGap);
  }

  && > ${ButtonGroupWrapper} {
    align-items: center;
    max-height: none;
    padding-top: 0;
    column-gap: var(--bannerDescriptionButtonsColumnGap);
  }

  /* An event that has not started gates every button off; the group is still
   * mounted and would otherwise spend a row gap on nothing. */
  && > ${ButtonGroupWrapper}:empty {
    display: none;
  }

  /*
   * At 1920 the countdown sits in the opposite bottom corner of the banner,
   * on the same paddings, positioned against the PosterContent this lives in.
   * From 1240 down the concept already places it in this column; Font4xl and
   * these paddings also step at 1640, so that is where the 1920 tier begins.
   */
  && > ${CountdownTimerSC} {
    box-sizing: border-box;
    position: absolute;
    right: 0;
    bottom: 0;
    padding: var(--bannerDescriptionPaddingVertical)
      var(--bannerDescriptionPaddingHorizontal);
  }

  ${MEDIA.MAX_1639} {
    --bannerDescriptionPaddingVertical: var(${FIXED_INDENTS.INDENT_120});
    --bannerDescriptionPaddingHorizontal: var(${FIXED_INDENTS.INDENT_160});

    && > ${CountdownTimerSC} {
      position: static;
      padding: 0;
    }
  }

  ${MEDIA.MAX_1239} {
    --bannerDescriptionPaddingVertical: var(${FIXED_INDENTS.INDENT_60});
    --bannerDescriptionPaddingHorizontal: var(${FIXED_INDENTS.INDENT_80});
    --bannerDescriptionRowGap: var(${FIXED_INDENTS.INDENT_60});
    --mcLabelsGroupGap: var(${FIXED_INDENTS.INDENT_60});
    --mcContentBadgesGroupGap: var(${FIXED_INDENTS.INDENT_20});
  }

  ${MEDIA.MAX_599} {
    --bannerDescriptionPaddingHorizontal: var(${FIXED_INDENTS.INDENT_40});
    --bannerDescriptionRowGap: var(${FIXED_INDENTS.INDENT_50});
    --bannerDescriptionButtonsColumnGap: var(${FIXED_INDENTS.INDENT_20});
    --mcTitleWithLabelsRowGap: var(${FIXED_INDENTS.INDENT_20});
    --mcLabelsGroupGap: var(${FIXED_INDENTS.INDENT_60});
    --mcContentBadgesGroupGap: var(${FIXED_INDENTS.INDENT_20});

    /*
     * The primary action takes whatever the icon buttons leave, so its width
     * follows their count. It navigates through a link on most states, so the
     * growing flex item is not always the button, and a button keeps its
     * intrinsic width until it is told to fill its box.
     */
    && > ${ButtonGroupWrapper} {
      width: 100%;

      > :first-child {
        flex: 1 1 auto;
        min-width: 0;
      }

      > :first-child${ButtonSC}[data-arcana-button='true'],
      > :first-child > ${ButtonSC}[data-arcana-button='true'] {
        width: 100%;
        min-width: 0;
        /*
         * Figma 375: 24px sides so the full Resume/Play title fits beside
         * the icon buttons. Keep 12px top/bottom so height stays 48px.
         */
        padding: var(${FIXED_INDENTS.INDENT_30}) var(${FIXED_INDENTS.INDENT_60});
      }
    }
  }
`
